import React, { createContext, useContext, useState, useEffect } from 'react'
import { grantSentryConsent, revokeSentryConsent } from 'setup/sentry'
import { CollectionId, getRecord, updateRecordFields } from 'utils/firestore'

type ConsentContextValue = {
  hasConsented: boolean | undefined
  isLoading: boolean
  grantConsent: () => void
  revokeConsent: () => void
}

const ConsentContext = createContext<ConsentContextValue>({
  hasConsented: undefined,
  isLoading: true,
  grantConsent: () => {},
  revokeConsent: () => {}
})

export function ConsentProvider({
  userId,
  children
}: {
  userId: string
  children: React.ReactNode
}) {
  const [hasConsented, setHasConsented] = useState<boolean | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    getRecord(CollectionId.Users, userId).then((doc) => {
      if (doc?.consentResponse === 'granted') {
        setHasConsented(true)
        grantSentryConsent()
      } else if (doc?.consentResponse === 'denied') {
        setHasConsented(false)
        revokeSentryConsent()
      } else {
        setHasConsented(undefined)
      }
      setIsLoading(false)
    })
  }, [userId])

  const grantConsent = () => {
    setHasConsented(true)
    grantSentryConsent()
    updateRecordFields(CollectionId.Users, userId, { consentResponse: 'granted' })
  }

  const revokeConsent = () => {
    setHasConsented(false)
    revokeSentryConsent()
    updateRecordFields(CollectionId.Users, userId, { consentResponse: 'denied' })
  }

  return (
    <ConsentContext.Provider value={{ hasConsented, isLoading, grantConsent, revokeConsent }}>
      {children}
    </ConsentContext.Provider>
  )
}

export const useConsent = () => useContext(ConsentContext)
